'use client'

import { useEffect, useRef } from 'react'
import { signOut } from '@/lib/auth'
import { useBillingInfo } from '@/hooks/useBillingInfo'
import { useAuth } from '@/hooks/useAuth'

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const

export function EnterpriseSecurityEnforcer() {
  const { isAnonymous, isLoading: authLoading } = useAuth()
  const { billingInfo } = useBillingInfo()
  const timeoutRef = useRef<number | null>(null)
  const signingOutRef = useRef(false)

  useEffect(() => {
    const timeoutMs = billingInfo?.enterpriseSecurity?.sessionInactivityTimeoutMs
    if (authLoading || isAnonymous || !billingInfo?.enterpriseSecurity?.sessionInactivityTimeoutEnabled || !timeoutMs) {
      return
    }
    if (typeof window === 'undefined') return

    const clearTimer = () => {
      if (timeoutRef.current === null) return
      window.clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    function resetTimer() {
      clearTimer()
      timeoutRef.current = window.setTimeout(() => {
        void terminateSession()
      }, timeoutMs)
    }

    async function terminateSession() {
      if (signingOutRef.current) return
      signingOutRef.current = true
      clearTimer()
      try {
        await signOut()
        window.location.href = '/'
      } catch (err) {
        console.error('[EnterpriseSecurityEnforcer] failed to sign out inactive session:', err)
        signingOutRef.current = false
        resetTimer()
      }
    }

    for (const eventName of ACTIVITY_EVENTS) {
      window.addEventListener(eventName, resetTimer, { passive: true })
    }
    resetTimer()

    return () => {
      clearTimer()
      for (const eventName of ACTIVITY_EVENTS) {
        window.removeEventListener(eventName, resetTimer)
      }
    }
  }, [
    authLoading,
    billingInfo?.enterpriseSecurity?.sessionInactivityTimeoutEnabled,
    billingInfo?.enterpriseSecurity?.sessionInactivityTimeoutMs,
    isAnonymous,
  ])

  return null
}
